import { z } from 'zod';

/**
 * F33 — trainer certification document + admin review (D5/D16). Independent of
 * F09's identity blue-tick. Doc lands in the private SSE-KMS `trainer-cert-docs`
 * bucket; admin reviews per-cert. The trainer-level
 * `trainer_profiles.verification_status` is a SEPARATE explicit admin decision.
 */

export const TRAINER_CERT_TITLE_MAX = 120;
export const TRAINER_CERT_ISSUING_BODY_MAX = 120;
export const TRAINER_CERT_MAX_BYTES = 5 * 1024 * 1024; // 5 MB (D16)
export const TRAINER_CERT_MAX_PER_PROFILE = 10; // D16
export const TRAINER_CERT_ACCEPTED_MIME = [
  'application/pdf',
  'image/jpeg',
  'image/png',
  'image/heic',
  'image/webp',
] as const;

export const TRAINER_CERT_STATUS_VALUES = ['pending', 'verified', 'rejected', 'revoked'] as const;
export type TrainerCertificationStatus = (typeof TRAINER_CERT_STATUS_VALUES)[number];
export const TrainerCertificationStatusSchema = z.enum(TRAINER_CERT_STATUS_VALUES);

/** Owner/admin view (includes the private s3 key + review metadata). */
export type TrainerCertification = {
  readonly id: string;
  readonly trainerUserId: string;
  readonly title: string;
  readonly issuingBody: string | null;
  readonly issuedYear: number | null;
  readonly status: TrainerCertificationStatus;
  readonly fileName: string;
  readonly mimeType: string;
  readonly sizeBytes: number;
  readonly reviewedAt: string | null;
  readonly rejectionReason: string | null;
  readonly createdAt: string;
  readonly updatedAt: string;
};

/** Public projection — title + issuing body + verified flag only; NO doc/file. */
export type PublicTrainerCertification = {
  readonly id: string;
  readonly title: string;
  readonly issuingBody: string | null;
  readonly issuedYear: number | null;
  readonly verified: boolean;
};

// ─── Input schemas ───────────────────────────────────────────────────────────

export const TrainerCertUploadUrlInputSchema = z
  .object({
    fileName: z.string().min(1).max(255),
    mimeType: z.enum(TRAINER_CERT_ACCEPTED_MIME),
    sizeBytes: z.number().int().min(1).max(TRAINER_CERT_MAX_BYTES),
  })
  .strict();
export type TrainerCertUploadUrlInput = z.infer<typeof TrainerCertUploadUrlInputSchema>;

export type TrainerCertUploadUrlResponse = {
  readonly url: string;
  readonly headers: Readonly<Record<string, string>>;
  readonly s3Key: string;
  readonly expiresAt: string;
};

/** Finalize a cert after the client PUTs the doc to S3. */
export const CreateTrainerCertInputSchema = z
  .object({
    title: z.string().min(1).max(TRAINER_CERT_TITLE_MAX),
    issuingBody: z.string().max(TRAINER_CERT_ISSUING_BODY_MAX).nullable().optional(),
    issuedYear: z.number().int().min(1950).max(2100).nullable().optional(),
    s3Key: z.string().min(1),
    fileName: z.string().min(1).max(255),
    mimeType: z.enum(TRAINER_CERT_ACCEPTED_MIME),
  })
  .strict();
export type CreateTrainerCertInput = z.infer<typeof CreateTrainerCertInputSchema>;

/** Admin per-cert review (`/admin/trainer-certifications/:certId/{verify,reject,revoke}`). */
export const ReviewTrainerCertInputSchema = z
  .object({
    rejectionReason: z.string().min(1).max(500).optional(),
  })
  .strict();
export type ReviewTrainerCertInput = z.infer<typeof ReviewTrainerCertInputSchema>;
